/**
 * TaskCard - Reusable compact card for Action Calendar views.
 *
 * Used in Day, 3-Day, and Schedule views.
 * Shows task title, status, estimate, and optional dependency hint.
 */

import React from 'react';
import { Box, Typography, Chip, Avatar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { StepStatus, Participant } from '../../../types/generated/graphql';
import { ScheduledStep, formatEffort } from '../../../utils/calendarScheduler';
import { colors } from '../../../theme/colors';

export type TaskCardVariant = 'full' | 'compact';

interface TaskCardProps {
  scheduledStep: ScheduledStep;
  variant?: TaskCardVariant;
  assignee?: Participant | null;
  isWaiting?: boolean;
  onClick: () => void;
}

/**
 * Get status icon for the task.
 */
function getStatusIcon(step: ScheduledStep['step'], isBlocked: boolean) {
  if (step.status === StepStatus.Completed) {
    return <CheckCircleIcon sx={{ fontSize: 16, color: colors.green[600] }} />;
  }
  if (step.status === StepStatus.Skipped) {
    return <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: colors.gray[400] }} />;
  }
  if (isBlocked) {
    return <LockOutlinedIcon sx={{ fontSize: 16, color: colors.amber[600] }} />;
  }
  return <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: colors.gray[400] }} />;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  scheduledStep,
  variant = 'full',
  assignee,
  isWaiting = false,
  onClick,
}) => {
  const { step, isBlocked, waitingOnStep, anchorHint, effort } = scheduledStep;
  const isPruned = step.status === StepStatus.Skipped;
  const isCompleted = step.status === StepStatus.Completed;

  // Truncate waiting on step name if too long
  const truncatedWaitingOn =
    waitingOnStep && waitingOnStep.length > 20
      ? waitingOnStep.slice(0, 17) + '...'
      : waitingOnStep;

  const isCompact = variant === 'compact';

  return (
    <Box
      onClick={onClick}
      sx={{
        p: isCompact ? 1.25 : 1.5,
        borderRadius: 1.5,
        border: `1px solid ${isWaiting ? colors.gray[300] : colors.border}`,
        bgcolor: isWaiting ? colors.gray[50] : colors.card,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        opacity: isPruned ? 0.6 : isBlocked ? 0.75 : isWaiting ? 0.85 : 1,
        '&:hover': {
          borderColor: colors.gray[300],
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Header row: status icon + title + optional estimate */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        {/* Status icon */}
        <Box sx={{ mt: 0.25, flexShrink: 0 }}>
          {getStatusIcon(step, isBlocked)}
        </Box>

        {/* Title and secondary info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: isCompact ? '0.8rem' : '0.875rem',
              lineHeight: 1.3,
              color: isCompleted ? colors.gray[500] : colors.textPrimary,
              textDecoration: isCompleted ? 'line-through' : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: isCompact ? 1 : 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {step.title}
          </Typography>

          {/* Secondary line: waiting on or anchor hint */}
          {!isCompact && (waitingOnStep || anchorHint) && (
            <Typography
              variant="caption"
              sx={{
                color: anchorHint ? colors.blue[600] : colors.gray[400],
                fontSize: '0.65rem',
                mt: 0.25,
                display: 'block',
              }}
            >
              {anchorHint || (waitingOnStep ? `After: ${truncatedWaitingOn}` : '')}
            </Typography>
          )}
        </Box>

        {/* Right side: assignee + estimate */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {/* Assignee avatar */}
          {assignee && !isCompact && (
            <Avatar
              sx={{
                width: 20,
                height: 20,
                fontSize: '0.6rem',
                bgcolor: colors.blue[100],
                color: colors.blue[700],
              }}
            >
              {assignee.name.charAt(0).toUpperCase()}
            </Avatar>
          )}

          {/* Effort chip */}
          <Chip
            label={formatEffort(effort)}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 500,
              bgcolor: 'transparent',
              color: colors.gray[500],
              border: `1px solid ${colors.gray[200]}`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>
      </Box>

      {/* Compact mode: show hint inline if needed */}
      {isCompact && waitingOnStep && !isBlocked && (
        <Typography
          variant="caption"
          sx={{
            color: colors.gray[400],
            fontSize: '0.6rem',
            mt: 0.5,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          After: {truncatedWaitingOn}
        </Typography>
      )}

      {/* Blocked indicator */}
      {isBlocked && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.75,
          }}
        >
          <Chip
            label="Blocked"
            size="small"
            sx={{
              height: 16,
              fontSize: '0.55rem',
              bgcolor: colors.amber[50],
              color: colors.amber[700],
              border: `1px solid ${colors.amber[200]}`,
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
          {waitingOnStep && (
            <Typography
              variant="caption"
              sx={{
                color: colors.gray[400],
                fontSize: '0.55rem',
              }}
            >
              {truncatedWaitingOn}
            </Typography>
          )}
        </Box>
      )}

      {/* Pruned indicator */}
      {isPruned && !isBlocked && (
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label="Inactive"
            size="small"
            sx={{
              height: 16,
              fontSize: '0.55rem',
              bgcolor: colors.gray[100],
              color: colors.gray[500],
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        </Box>
      )}

      {/* Waiting indicator */}
      {isWaiting && !isBlocked && !isPruned && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.75,
          }}
        >
          <Chip
            label="Waiting"
            size="small"
            sx={{
              height: 16,
              fontSize: '0.55rem',
              bgcolor: colors.gray[100],
              color: colors.gray[600],
              border: `1px solid ${colors.gray[200]}`,
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
          {assignee && (
            <Typography
              variant="caption"
              sx={{
                color: colors.gray[500],
                fontSize: '0.55rem',
              }}
            >
              on {assignee.name}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TaskCard;
